/**
 * The Declination Study's commentary, in the same fixed-width form as the Longitude Study's.
 *
 * <h2>Shared with the longitude report, and why</h2>
 *
 * Total instances, pivot counts and the directional move into and out of each event are computed
 * by {@link module:utils/longitudeStudyReport}, not reimplemented here. They are questions about
 * the PRICE around a set of dates and have nothing to do with what produced the dates — two
 * implementations of them would be two chances to disagree, and a trader comparing a longitude
 * study with a declination study needs the two numbers to mean the same thing.
 *
 * <h2>What is specific to declination</h2>
 *
 * Longitude is an angle around a circle: a body is somewhere on it and that is all. Declination has
 * a shape — a body climbs to a northern extreme, falls back through the celestial equator to a
 * southern one, and returns. Where in that cycle an aspect falls is most of what it means, so the
 * commentary reports the extremes, the equator crossings, and how long the two bodies spent on the
 * same side of the equator as against opposite sides.
 *
 * That context comes from the backend, which already holds the ephemeris. It is optional
 * throughout: an older backend sends no summary, and the report then prints its price half and
 * says the astronomical half is unavailable rather than printing nothing.
 */

import { computeLongitudeStudyStats, MOVE_WINDOW_BARS, PIVOT_WINDOW_BARS } from './longitudeStudyReport';

const RULE = '='.repeat(50);
const SUB = ' ' + '-'.repeat(49);

/** Declination in degrees with a hemisphere letter, e.g. `23.44 N`. */
function declination(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${Math.abs(value).toFixed(2)}° ${value >= 0 ? 'N' : 'S'}`;
}

function pct(count, base) {
  if (!base) return '  0.0';
  return (count * 100 / base).toFixed(1).padStart(5);
}

/** The aspect name as a reader knows it, from the backend's constant. */
export function aspectLabel(aspect) {
  switch (String(aspect || '').toUpperCase()) {
    case 'PARALLEL': return 'Parallel';
    case 'CONTRAPARALLEL': return 'Contra-parallel';
    case 'NPARALLEL': return 'Parallel North';
    case 'SPARALLEL': return 'Parallel South';
    default: return aspect || 'Declination aspect';
  }
}

/**
 * Statistics for one Declination Study run.
 *
 * @param eventDates dates the aspect occurred
 * @param barData    the loaded chart
 * @param summary    the backend's DeclinationSummary, or null
 */
export function computeDeclinationStudyStats(eventDates, barData, summary, options = {}) {
  const price = computeLongitudeStudyStats(eventDates, barData, options);
  return { ...price, summary: summary || null };
}

/** One body's declination behaviour, as three aligned lines. */
function planetBlock(planet) {
  if (!planet) return [];
  const crossings = planet.equatorCrossings;
  return [
    `   ${String(planet.name || '?').padEnd(9)} high  : ${declination(planet.maxNorth)}`
      + (planet.maxNorthDate ? `  on ${planet.maxNorthDate}` : ''),
    `   ${' '.repeat(9)} low   : ${declination(planet.maxSouth)}`
      + (planet.maxSouthDate ? `  on ${planet.maxSouthDate}` : ''),
    `   ${' '.repeat(9)} 0°    : ${crossings} crossing${crossings === 1 ? '' : 's'}`
      + `, ended ${planet.endedHemisphere || '?'}`,
  ];
}

/**
 * The report, as a fixed-width block.
 *
 * Line width is held under sixty characters for the same reason the longitude report is: the
 * journal panel wraps on width, and a wrapped line destroys the alignment the format exists for.
 */
export function formatDeclinationStudyReport({ planet1, planet2, aspect, frame, stats }) {
  const s = stats;
  const summary = s.summary;

  const counts = [s.into.l2h, s.into.h2l, s.outOf.l2h, s.outOf.h2l];
  const w = Math.max(2, ...counts.map(c => String(c).length));
  const cell = (count, base) => `${String(count).padStart(w)} (${pct(count, base)}%)`;

  const lines = [];
  lines.push(RULE);
  lines.push(` ${planet1} ${aspectLabel(aspect)} ${planet2} (${frame})`);
  lines.push(RULE);
  lines.push('');
  lines.push(` Total Instances : ${s.totalInstances}`);
  lines.push('');

  // --- the declination-specific half -----------------------------------------------------
  if (summary) {
    lines.push(' Declination Extremes');
    lines.push(SUB);
    planetBlock(summary.planet1).forEach(l => lines.push(l));
    planetBlock(summary.planet2).forEach(l => lines.push(l));
    lines.push('');

    const total = (summary.bothNorthDays || 0) + (summary.bothSouthDays || 0)
      + (summary.oppositeSideDays || 0);
    lines.push(' Hemisphere Agreement');
    lines.push(SUB);
    lines.push(`   Both north    : ${String(summary.bothNorthDays).padStart(w)} (${pct(summary.bothNorthDays, total)}%)`);
    lines.push(`   Both south    : ${String(summary.bothSouthDays).padStart(w)} (${pct(summary.bothSouthDays, total)}%)`);
    lines.push(`   Opposite      : ${String(summary.oppositeSideDays).padStart(w)} (${pct(summary.oppositeSideDays, total)}%)`);
    lines.push('');

    lines.push(' Aspect Frequency');
    lines.push(SUB);
    lines.push(`   Parallel      : ${summary.parallelCount}`);
    lines.push(`   Contra-par.   : ${summary.contraParallelCount}`);
    lines.push('');
  }

  // --- the price half, identical in meaning to the longitude report -----------------------
  lines.push(` Pivot Count (+/- ${s.pivotWindow} bars)`);
  lines.push(SUB);
  lines.push(`   High Pivots   : ${s.highPivots}`);
  lines.push(`   Low Pivots    : ${s.lowPivots}`);
  lines.push('');
  lines.push(` Directional Price Movement (+/- ${s.moveWindow} bars)`);
  lines.push(SUB);
  lines.push(`   Into Aspect   | L2H: ${cell(s.into.l2h, s.into.evaluated)}`
    + `  | H2L: ${cell(s.into.h2l, s.into.evaluated)}`);
  lines.push(`   Out of Aspect | L2H: ${cell(s.outOf.l2h, s.outOf.evaluated)}`
    + `  | H2L: ${cell(s.outOf.h2l, s.outOf.evaluated)}`);

  const notes = [];
  if (!summary) {
    notes.push('declination context unavailable from the server');
  }
  if (s.datesOffChart > 0) {
    notes.push(`${s.datesOffChart} event date(s) fell outside the loaded chart range`);
  }
  if (s.pivotsNotJudged > 0) {
    notes.push(`${s.pivotsNotJudged} instance(s) too near the chart edge to judge a pivot`);
  }
  const notJudged = Math.max(s.into.notJudged, s.outOf.notJudged);
  if (notJudged > 0) {
    notes.push(`${notJudged} instance(s) lack ${s.moveWindow} bars either side`);
  }
  if (notes.length > 0) {
    lines.push('');
    lines.push(' Notes');
    lines.push(SUB);
    notes.forEach(n => lines.push(`   - ${n}`));
  }

  lines.push('');
  lines.push(RULE);
  return lines.join('\n');
}

/** Convenience: compute and format in one call. */
export function buildDeclinationStudyReport({
  planet1, planet2, aspect, frame, eventDates, barData, summary, options,
}) {
  const stats = computeDeclinationStudyStats(eventDates, barData, summary, options);
  return {
    stats,
    report: formatDeclinationStudyReport({ planet1, planet2, aspect, frame, stats }),
  };
}

export { MOVE_WINDOW_BARS, PIVOT_WINDOW_BARS };
